import { useRef } from "react";
import { readUrlParams } from "./urlParams.js";

// Push-coalescing URL-param merger — the single writer every navigation in App
// routes through. Returns `mergeUrlParams(mutate, { push })`.
//
// Reads FRESH from the URL, not react-router's `prev`: dcol/xcol (SplitPage /
// ContentShell) write via writeUrlParam → history.replaceState, which doesn't
// notify react-router, so `prev` would be stale and silently clobber them.
// readUrlParams() reads window.location.search, so the helper merges the caller's
// change on top of whatever is actually in the URL.
//
// `push: true` opens a new browser-history entry so back/forward can step to/from
// this URL — for genuine navigations (scope change, mode/view switch, tab
// open/close/activate). The default (replace) is for transient mirror writes
// (lens/tab/log/ui cache, panel layout) that fire constantly and would otherwise
// flood the history stack with entries the user never meant to navigate.
//
// PUSH COALESCING: the FIRST push in a synchronous tick opens a new history entry;
// every later push in the same tick REPLACES onto it. So one click that changes
// scope AND focus AND view (several mergeUrlParams calls in one handler, incl.
// updateScopePath's transition) is ONE back/forward stop, not three — and a
// navigation's focus/view can never land on and clobber the PREVIOUS entry.
// readUrlParams reads window.location, which each setSearchParams updates
// synchronously, so every mutate in the burst sees the prior write. The flag
// resets on the next microtask (after the click handler's synchronous writes).
export function useUrlParamMerger(setSearchParams) {
    const pushedThisTick = useRef(false);
    return (mutate, { push = false } = {}) => {
        let openEntry = push;
        if (push) {
            if (pushedThisTick.current) openEntry = false;
            else {
                pushedThisTick.current = true;
                queueMicrotask(() => { pushedThisTick.current = false; });
            }
        }
        setSearchParams(() => {
            const next = readUrlParams();
            mutate(next);
            return next;
        }, { replace: !openEntry });
    };
}
